import React from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@asad/crane-ui'

import {
  DASHBOARD_CANVAS_MIN_HEIGHT,
  DASHBOARD_CARD_ROW_HEIGHT,
  DASHBOARD_CARD_WIDTH,
  normalizePositions,
  type DashboardView,
} from '../model/dashboardViews'
import type { DeviceStatusType, MapDevice } from '../types'
import { FleetDeviceCard } from './FleetDeviceCard'

type DashboardDeviceCardsProps = {
  devices: MapDevice[]
  activeView?: DashboardView
  statusById: Map<string, DeviceStatusType>
  selectedDeviceId?: string
  onOpenDetails: (deviceId: string) => void
  onCardRef: (deviceId: string, element: HTMLDivElement | null) => void
}

/** Siatka kart domyślnych albo płótno pozycji zapisanych w aktywnym Widoku. */
export const DashboardDeviceCards = ({
  devices,
  activeView,
  statusById,
  selectedDeviceId,
  onOpenDetails,
  onCardRef,
}: DashboardDeviceCardsProps) => {
  const { t } = useTranslation()
  const devicesById = React.useMemo(() => new Map(devices.map((device) => [device.id, device])), [devices])
  const positions = React.useMemo(
    () =>
      activeView
        ? normalizePositions(activeView.deviceIds, activeView.positions).filter(({ deviceId }) =>
            devicesById.has(deviceId),
          )
        : [],
    [activeView, devicesById],
  )

  if (!devices.length) return <EmptyState title={t('common.noResults')} hint={t('tables.emptyDescription')} />

  if (!activeView) {
    return (
      <div className='grid w-full auto-rows-min grid-cols-crane-autofill-lg gap-3'>
        {devices.map((device) => (
          <div key={device.id} ref={(element) => onCardRef(device.id, element)}>
            <FleetDeviceCard
              device={device}
              deviceStatus={statusById.get(device.id)}
              hasActiveBorder={device.id === selectedDeviceId}
              onSelect={onOpenDetails}
              compact
            />
          </div>
        ))}
      </div>
    )
  }

  /* Karty stoją dokładnie w pozycjach zapisanych w Widoku. Gdy panel jest węższy niż układ,
     płótno zachowuje szerokość układu, a ramka przewija się w poziomie — przycinanie pozycji
     do szerokości panelu nakładało karty na siebie. Pion przewija dalej cała strona. */
  return (
    <div className='overflow-x-auto rounded-crane-md bg-crane-card-inset p-3.5'>
      <div
        className='relative min-h-80'
        style={{
          minWidth: Math.max(0, ...positions.map(({ x }) => x + DASHBOARD_CARD_WIDTH)),
          height: Math.max(DASHBOARD_CANVAS_MIN_HEIGHT, ...positions.map(({ y }) => y + DASHBOARD_CARD_ROW_HEIGHT)),
        }}
      >
        {positions.map((position) => {
          const device = devicesById.get(position.deviceId)
          return device ? (
            <div
              key={device.id}
              ref={(element) => onCardRef(device.id, element)}
              className='absolute min-w-0'
              style={{ left: position.x, top: position.y, width: DASHBOARD_CARD_WIDTH }}
            >
              <FleetDeviceCard
                device={device}
                deviceStatus={statusById.get(device.id)}
                hasActiveBorder={selectedDeviceId === device.id}
                onSelect={onOpenDetails}
                compact
              />
            </div>
          ) : null
        })}
      </div>
    </div>
  )
}
